// 장비·바닥 드롭 보기 타입(D2a·D2b). 게임(game/gear.ts·loot.ts)이 채우고 렌더·UI·오디오가 읽는다. 타입만.
import type { EquipSlot, HeroId } from './content/types';
import { makeGearTotals, type GearItem, type GearTotals } from './gear';
import type { EnhanceLog, GearCode } from './gearLedger';

export type GearMode = 'ok' | 'corrupt' | 'future' | 'unavailable';
export type GearCmdKind = 'none' | 'equip' | 'unequip' | 'enhance' | 'salvage' | 'buyProtect' | 'pickup' | 'recover'
  // D2c: 상점 구매·비약 제작·소비 아이템 사용·비약·보석 박기/빼기/합성
  | 'buy' | 'craft' | 'use' | 'elixir' | 'socket' | 'unsocket' | 'combine'
  // S7: 마을 장비 상점 구매
  | 'buyGear'
  // C4: 숙련 배분·초월 강화·숙련 이정표
  | 'msAlloc' | 'transcend' | 'msClaim';
/** 마지막 명령 결과(UI 문구·효과음). 같은 객체를 재사용하고 seq로 새 결과를 안다 */
export interface GearResultView {
  readonly seq: number;
  readonly kind: GearCmdKind;
  readonly code: GearCode;
  readonly uid: string;
  readonly requestId: string;
  /** 강화 결과(강화가 아니면 '') */
  readonly outcome: '' | 'success' | 'keep' | 'down' | 'protected';
  readonly from: number;
  readonly to: number;
  readonly chance: number;
}
/**
 * 이미 성공이라 알린 연산(줍기·장착·강화 등)이 나중에 저장소 최신 기준 위에서 거절됐다는 알림(c015, PR #154 리뷰 이월 5번).
 * 예: 다른 탭이 오래된 기준으로 덮어써 이 탭 강화를 다시 적용했는데 그 사이 강화석이 모자라 no_stones. 같은 객체를 재사용하고 seq로 새 알림을 안다
 */
export interface GearNoticeView {
  readonly seq: number;
  /** 거절된 연산 종류(강화석 줍기도 'pickup') */
  readonly kind: GearCmdKind;
  readonly code: GearCode;
  readonly uid: string;
}
export interface GearView {
  readonly mode: GearMode;
  /** 바뀔 때마다 1 증가(UI 재그리기 키). 던전 안 줍기에도 오른다 */
  readonly revision: number;
  /** 가방 + 장착 장비(장착은 equipped가 uid로 가리킨다) */
  readonly items: readonly GearItem[];
  /** 지금 영웅이 보는 장착(무기 = 그 영웅 무기 칸, lyra-weapon-slot) */
  readonly equipped: Readonly<Record<EquipSlot, string | null>>;
  /** 영웅별 무기 칸 전체(다른 영웅이 낀 무기는 가방에서 빼고 '○○ 착용 중'으로 보인다). 없는 옛 뷰·픽스처는 equipped만 본다 */
  readonly heroWeapons?: Readonly<Partial<Record<HeroId, string>>>;
  readonly stones: readonly number[];
  readonly protects: number;
  /** 장비 경제 순지출 골드(강화·주문서 − 판매, 미기록 포함 화면 값). 골드 잔액은 committedGoldNet(확인 안 된 수입 제외)을 뺀다 */
  readonly goldNet: number;
  readonly committedGoldNet: number;
  readonly log: readonly EnhanceLog[];
  /** 아직 저장하지 못한 연산 수(줍기 등) */
  readonly pendingOps: number;
  readonly lastSaveOk: boolean;
  readonly last: GearResultView;
  /** 성공이라 알린 연산이 나중에 거절됐다는 알림(Ev.GearNotice와 함께 오른다) */
  readonly notice: GearNoticeView;
  /** 장착 장비 합계(장착·강화가 바뀔 때만 다시 계산) */
  readonly totals: GearTotals;
  /** 무기를 뺀 장착 합계(c022: 무기 칸이 하나라 카엘 전용 무기는 다른 영웅 능력치에 넣지 않는다) */
  readonly totalsNoWeapon: GearTotals;
  /** 장착 무기의 영웅 전용(heroLock, 무기가 없거나 전용이 없으면 null). gearTotalsOf가 무기 합계를 넣을 영웅을 고른다. 없는 옛 뷰는 카엘 전용으로 본다 */
  readonly weaponLock?: string | null;
  /** 지금 강화 상한(마을·지역6 클리어)과 보호 주문서 해금(지역6 클리어) */
  readonly enhanceCap: number;
  readonly protectUnlocked: boolean;
  /** 도착한 가장 높은 마을(상점 단계·보호 주문서 가격) */
  readonly town: number;
  /** D2c 소비 아이템 보유(id → 수), 보석 보유(코드 → 수), 비약 끝 시각(플레이 초), 지금 플레이 시계(초) */
  readonly cons: Readonly<Record<string, number>>;
  readonly gems: readonly number[];
  readonly elix: readonly number[];
  readonly play: number;
}

/** 렌더·UI가 읽는 바닥 드롭 풀(고정 배열, 같은 인덱스 = 같은 드롭) */
export interface LootView {
  readonly capacity: number;
  readonly active: Uint8Array;
  readonly x: Float32Array;
  readonly z: Float32Array;
  /** 0 장비, 1 강화석, 2 보석 */
  readonly kind: Uint8Array;
  /** 등급 번호(GRADE_INDEX, 강화석은 0) */
  readonly grade: Uint8Array;
  /** 떨어진 뒤 지난 틱(튀어나오는 연출·줍기 대기) */
  readonly age: Int32Array;
  /** 이번 판 드롭·줍기 수(QA 집계) */
  readonly drops: number;
  readonly pickups: number;
  readonly petPickups: number;
  /** 동행 펫 줍기 반경(m, 0 = 펫 없음) */
  readonly petRadius: number;
  /** 드롭 장비 개체(kind 0, 드롭 식별: 월드 이름표가 이름·그림을 읽는다). 줍거나 치우면 null */
  readonly item: (GearItem | null)[];
  /** 종류별 보조값: 장비 = 부위 번호(gearLedger.SLOT_ORDER), 강화석 = 개수, 보석 = 보석 코드 */
  readonly aux: Int32Array;
  /** 강화석 등급 0..3(economy STONE_TIERS) */
  readonly tier: Uint8Array;
  /** 방금 주운 장비 개체 고리(Ev.LootPickup y = 고리 칸 + 1, 0 = 없음). 줍기 알림 띠가 이름·그림·비교를 읽는다 */
  readonly picked: (GearItem | null)[];
}
/** 줍기 고리 칸 수(한 틱에 판 전체를 거둬도 안 넘치게 드롭 풀보다 크다) */
export const PICKED_RING = 64;

/** 픽스처·갤러리용 빈 장비 보기(게임 없이 렌더·UI를 띄울 때) */
export function emptyGearView(): GearView {
  return {
    mode: 'ok', revision: 0, items: [], equipped: { weapon: null, helm: null, armor: null, gloves: null, boots: null, amulet: null },
    stones: [0, 0, 0, 0], protects: 0, goldNet: 0, committedGoldNet: 0, log: [], pendingOps: 0, lastSaveOk: true,
    last: { seq: 0, kind: 'none', code: 'ok', uid: '', requestId: '', outcome: '', from: 0, to: 0, chance: 0 },
    notice: { seq: 0, kind: 'none', code: 'ok', uid: '' },
    totals: makeGearTotals(), totalsNoWeapon: makeGearTotals(), weaponLock: null, enhanceCap: 5, protectUnlocked: false, town: 1,
    cons: {}, gems: new Array<number>(15).fill(0), elix: [0, 0, 0, 0], play: 0,
  };
}
/** 픽스처·갤러리용 물약 퀵슬롯 보기(D2c) */
export function emptyConsumeView(): import('./types').ConsumeView {
  return { potionCd: 0, potionCdMax: 360, decay: 1, autoPotion: 0, featherUsed: false, slotItem: ['', ''], used: 0 };
}
/** 픽스처용 빈 드롭 풀 */
export function emptyLootView(capacity = 48): LootView {
  return {
    capacity, active: new Uint8Array(capacity), x: new Float32Array(capacity), z: new Float32Array(capacity), kind: new Uint8Array(capacity),
    grade: new Uint8Array(capacity), age: new Int32Array(capacity), drops: 0, pickups: 0, petPickups: 0, petRadius: 0,
    item: new Array<GearItem | null>(capacity).fill(null), aux: new Int32Array(capacity), tier: new Uint8Array(capacity),
    picked: new Array<GearItem | null>(PICKED_RING).fill(null),
  };
}

/**
 * 그 영웅이 실제 전투에서 쓰는 장착 합계(장착 무기가 그 영웅 것이거나 전용이 없으면 무기 포함, 다른 영웅 전용이면 무기 제외). 화면 합계가 전투 능력치와 같게 하는 단일 출처.
 * 영웅 미지정 = 카엘. weaponLock이 없는 옛 뷰는 무기를 카엘 전용으로 본다(c022 동작 그대로). 라이라 활이 라이라 피해에 들어가지 않던 결함(lyra-weapon-damage) 수정
 */
export function gearTotalsOf(gear: Pick<GearView, "totals" | "totalsNoWeapon" | "weaponLock"> | undefined, heroId: string | undefined): GearTotals | undefined {
  if (!gear) return undefined;
  const lock = gear.weaponLock === undefined ? "kael" : gear.weaponLock;
  return lock === null || lock === (heroId ?? "kael") ? gear.totals : gear.totalsNoWeapon;
}
